import { z } from "zod";
import {
  completionNeedsToDecision,
  type CompletionNeed,
} from "./completion-request";
import {
  MEDICAL_EVIDENCE_COPY,
  MEDICAL_EVIDENCE_TASK_KEYS,
  type MedicalEvidenceCustomTask,
  type MedicalEvidenceTask,
  type MedicalEvidenceTaskKey,
} from "./medical-evidence";
import type { MeetingNextStep } from "./meeting";
import type { CompletionGroup } from "./prescription-transfer-review";

// TES-950 (TES-941 §BESLUT OCH KOMPLETTERINGAR) — ONE completion picker for
// the doctor, used after a care meeting, in the prescription-transfer review
// and when a lab result is reviewed.
//
// Before this there were three vocabularies for the same question: the
// meeting's four fixed `nextStep` packages (each silently adding blood
// pressure and pulse), the transfer review's seven needs plus a required
// patient-visible reason (TES-909), and the lab review's three task keys.
// Emelie 2026-09-25:
//   "Fasta paket som automatiskt lägger till exempelvis blodtryck, puls och blodprov ska tas bort. Kirill ska välja exakt det som behövs."
//
// This file is the one vocabulary. What the patient's app renders is still
// the CP-06 checklist and its five task keys — the picker is translated onto
// them, so app 2.0.1 (which knows exactly those keys) needs no change.

// --- the decision ------------------------------------------------------------

export const COMPLETION_OUTCOMES = [
  "proceed",
  "completion",
  "cannot_proceed",
] as const;
export const completionOutcomeSchema = z.enum(COMPLETION_OUTCOMES);
export type CompletionOutcome = z.infer<typeof completionOutcomeSchema>;

// Her words, verbatim (TES-941). The stored outcome as history reads it: a
// meeting documented as `proceed` before Ö-526 kept its decision for later,
// and its journal and meeting list still say so.
export const COMPLETION_OUTCOME_LABEL: Record<CompletionOutcome, string> = {
  proceed: "Underlaget är tillräckligt – gå vidare",
  completion: "Komplettering behövs",
  cannot_proceed: "Kan inte gå vidare",
};

// TES-1400 (Ö-526, Emelie 2026-10-15) — what the doctor chooses between:
//   "Det finns tre publicerbara medicinska utfall: Godkänd för behandling · Komplettering behövs · Kan inte gå vidare. Underlaget är tillräckligt – gå vidare är inte ett fjärde utfall."
// `proceed` is stored as before (every reader of the column keeps its
// meaning), but it is now always taken with the treatment decision: the
// meeting's "document and decide" (TES-1185).
export const COMPLETION_OUTCOME_CHOICE_LABEL: Record<
  CompletionOutcome,
  string
> = {
  proceed: "Godkänd för behandling",
  completion: "Komplettering behövs",
  cannot_proceed: "Kan inte gå vidare",
};

// The five options, verbatim and in her order. No packages: each one is
// exactly one thing the patient is asked to do.
export const COMPLETION_ITEMS = [
  "blood_pressure_pulse",
  "previous_lab_results",
  "new_blood_test",
  "prescription_evidence",
  "other",
] as const;
export const completionItemSchema = z.enum(COMPLETION_ITEMS);
export type CompletionItem = z.infer<typeof completionItemSchema>;

export const COMPLETION_ITEM_LABEL: Record<CompletionItem, string> = {
  blood_pressure_pulse: "Blodtryck och vilopuls",
  previous_lab_results: "Ladda upp tidigare provsvar",
  new_blood_test: "Ta ett nytt blodprov",
  prescription_evidence:
    "Ladda upp dokumentation av aktuellt recept/ordination",
  other: "Annan specificerad komplettering",
};

// The CP-06 task each option raises from the fixed five. `other` raises none
// of them: since TES-1000 (Ö-246 (b), Emelie 2026-09-29) it is a task of its
// own — a `CustomCompletionTask` row with the doctor's heading and
// instruction, counted in "0 av X klara" beside these (see
// `medicalEvidenceCustomTaskSchema`). It used to be a chat message only.
export const COMPLETION_ITEM_TASK: Record<
  CompletionItem,
  MedicalEvidenceTaskKey | null
> = {
  blood_pressure_pulse: "blood_pressure_pulse",
  previous_lab_results: "previous_lab_results",
  new_blood_test: "new_blood_test",
  prescription_evidence: "prescription_evidence",
  other: null,
};

// TES-1000 (Ö-246) — the patient-facing heading of an "Annan specificerad
// komplettering". Short: it is a checklist row's title.
export const CUSTOM_COMPLETION_HEADING_MAX = 120;

// The chosen set, as every entry point sends it.
//
// TES-1000 (Ö-246) — "Annan specificerad komplettering" carries what the
// patient's row shows: `otherHeading` (the row's title) and `otherText` (the
// instruction), both required exactly when `other` is chosen — an
// unspecified "annan komplettering" is a request the patient cannot act on.
// `otherUploadAllowed` lets her attach files to the row.
const otherFields = {
  otherHeading: z.string().trim().max(CUSTOM_COMPLETION_HEADING_MAX).optional(),
  otherText: z.string().trim().max(2000).optional(),
  otherUploadAllowed: z.boolean().optional(),
};

function refineOther(
  v: {
    items: readonly CompletionItem[];
    otherHeading?: string;
    otherText?: string;
    otherUploadAllowed?: boolean;
  },
  ctx: z.RefinementCtx,
): void {
  const hasOther = v.items.includes("other");
  if (hasOther) {
    if (!v.otherHeading) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["otherHeading"],
        message: "other_heading_required",
      });
    }
    if (!v.otherText) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["otherText"],
        message: "other_text_required",
      });
    }
    return;
  }
  for (const key of ["otherHeading", "otherText"] as const) {
    if (v[key]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: "other_text_only_with_other",
      });
    }
  }
  if (v.otherUploadAllowed) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["otherUploadAllowed"],
      message: "other_text_only_with_other",
    });
  }
}

export const completionSelectionSchema = z
  .object({
    items: z.array(completionItemSchema).min(1),
    ...otherFields,
  })
  .superRefine(refineOther);
export type CompletionSelection = z.infer<typeof completionSelectionSchema>;

// The meeting's full decision: outcome plus, on "Komplettering behövs", the
// selection. The other two outcomes carry no items — a doctor who says the
// evidence is enough has not asked the patient for anything.
export const meetingCompletionDecisionSchema = z
  .object({
    outcome: completionOutcomeSchema,
    items: z.array(completionItemSchema).default([]),
    ...otherFields,
  })
  .superRefine((v, ctx) => {
    if (v.outcome === "completion") {
      const parsed = completionSelectionSchema.safeParse({
        items: v.items,
        otherHeading: v.otherHeading,
        otherText: v.otherText,
        otherUploadAllowed: v.otherUploadAllowed,
      });
      if (!parsed.success) {
        for (const issue of parsed.error.issues) ctx.addIssue(issue);
      }
      return;
    }
    if (
      v.items.length > 0 ||
      v.otherHeading ||
      v.otherText ||
      v.otherUploadAllowed
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["items"],
        message: "items_only_with_completion",
      });
    }
  });
export type MeetingCompletionDecision = z.infer<
  typeof meetingCompletionDecisionSchema
>;

// --- mapping onto CP-06 ------------------------------------------------------

// The chosen items as CP-06 task keys, in the checklist's canonical order so
// the stored list does not depend on click order.
//
// `lab_payment` is never an option. Whether a new blood test is paid is a
// rule of the entry point, not a choice in the picker:
//   - after a care meeting it is (CP-06 Variant B, TES-720) — `labPayment: true`;
//   - on a transfer komplettering it is not (TES-763: "riktad komplettering
//     skapar ingen implicit provbetalning");
//   - on a lab review it follows the doctor's own cost answer (Ö-227);
//   - on a patient-overview request it is only for a new start on the
//     optional care meeting track (TES-1640, Ö-955), applied by the case
//     (withCompletionRequests), not here.
export function completionItemsToTaskKeys(
  items: readonly CompletionItem[],
  opts: { labPayment: boolean },
): MedicalEvidenceTaskKey[] {
  const wanted = new Set<MedicalEvidenceTaskKey>();
  for (const item of items) {
    const task = COMPLETION_ITEM_TASK[item];
    if (task) wanted.add(task);
  }
  if (opts.labPayment && wanted.has("new_blood_test"))
    wanted.add("lab_payment");
  return MEDICAL_EVIDENCE_TASK_KEYS.filter((k) => wanted.has(k));
}

// The tasks a meeting decision opens. Only "Komplettering behövs" opens any:
// the patient gets an active completion task only once Kirill has chosen.
export function meetingCompletionTasks(
  decision: Pick<MeetingCompletionDecision, "outcome" | "items">,
): MedicalEvidenceTaskKey[] {
  if (decision.outcome !== "completion") return [];
  return completionItemsToTaskKeys(decision.items, { labPayment: true });
}

// The legacy `nextStep` a picker decision is stored under, so every reader of
// the meeting row (journey, CP-09 outcome events, the first cycle's lab gate
// `labsWaivedFor`, the lab payment) keeps its meaning.
//
// `nextStep` has always been the BLOOD-TEST branch, not the whole decision:
//   - a new blood test → labs first (`new_blood_test`);
//   - earlier results to upload → labs first (`upload_existing_labs`);
//   - no lab item at all → the labs are not what is waited for, which is the
//     old "verified" branch (`labs_verified_trusted_source`: the decision may
//     proceed and the first cycle is not gated on a lab order). That is what
//     option 3 meant when it asked only for blood pressure and pulse, and a
//     blood-pressure-only komplettering means the same now.
export function meetingNextStepForCompletion(
  decision: Pick<MeetingCompletionDecision, "outcome" | "items">,
): MeetingNextStep {
  if (decision.outcome === "cannot_proceed") return "cannot_proceed";
  if (decision.outcome === "completion") {
    if (decision.items.includes("new_blood_test")) return "new_blood_test";
    if (decision.items.includes("previous_lab_results"))
      return "upload_existing_labs";
  }
  return "labs_verified_trusted_source";
}

// The tasks a meeting documented BEFORE the picker opens, from its fixed
// package. Kept so an old case stays readable exactly as it was asked for.
export function legacyNextStepTasks(
  nextStep: MeetingNextStep,
  prescriptionEvidenceRequired: boolean,
): MedicalEvidenceTaskKey[] {
  const required: MedicalEvidenceTaskKey[] = [];
  if (nextStep === "upload_existing_labs")
    required.push("previous_lab_results");
  if (prescriptionEvidenceRequired) required.push("prescription_evidence");
  // TES-720 — CP-06 Variant B: the payment row gates the sample row.
  if (nextStep === "new_blood_test")
    required.push("lab_payment", "new_blood_test");
  if (nextStep !== "cannot_proceed") required.push("blood_pressure_pulse");
  return required;
}

// The transfer review keeps TES-909's routing: what F-07 already asks
// reopens its section, only what F-07 cannot ask becomes a CP-06 row. The
// five options are resolved through that table rather than beside it, so the
// two can never disagree about where the prescription evidence goes (an F-07
// section, `pt_prescription_upload`).
//
// Ö-243's assumption (a): the five replace the seven here too. The two needs
// that only the seven had (current medication and dose, and how the treatment
// has gone) are no longer offered, and `other` is its own checklist row
// (Ö-246 (b), TES-1000) where TES-909 reopened F-02's `other_medical`.
const TRANSFER_NEED: Record<CompletionItem, CompletionNeed | null> = {
  blood_pressure_pulse: "blood_pressure_and_pulse",
  previous_lab_results: "previous_lab_results",
  new_blood_test: "new_blood_test",
  prescription_evidence: "prescription_evidence",
  other: null,
};

export function completionSelectionToTransferDecision(
  items: readonly CompletionItem[],
): {
  completionGroups: CompletionGroup[];
  evidenceTasks: MedicalEvidenceTaskKey[];
} {
  const needs = items
    .map((i) => TRANSFER_NEED[i])
    .filter((n): n is CompletionNeed => n !== null);
  return completionNeedsToDecision(needs);
}

// Ö-243's assumption (a) also removes the required free-text reason. The app
// (2.0.1 included) shows the review's `reason` as the doctor's message, so the
// server stores this standard line in its place. TES-941, verbatim.
// TES-953 — one source: the checklist's own intro is the same line.
export const COMPLETION_STANDARD_PATIENT_TEXT: string =
  MEDICAL_EVIDENCE_COPY.sv.body;

// --- hints -------------------------------------------------------------------

// What the system already has, per option, so the doctor sees what is missing
// before he picks. Facts only: no staleness verdict (Ö-235), and nothing for a
// new blood test — without a Webdoc integration the system cannot know
// whether a result exists (TES-941: no "nytt blodprov: svar finns").
export const completionHintSchema = z.object({
  item: completionItemSchema,
  // Null when the system has no way to know (new blood test, other).
  present: z.boolean().nullable(),
  // When the evidence was taken or uploaded, if present.
  at: z.string().nullable(),
  hint: z.string().nullable(),
  // TES-1000 (Ö-244 (b), Emelie 2026-09-29) — never true any more: missing
  // blood pressure and pulse is a hint, not a tick. Kept in the shape so an
  // admin that still reads it gets `false` rather than a missing field.
  // "Systemet får inte automatiskt lägga till ett fast kompletteringspaket."
  preselect: z.boolean(),
});
export type CompletionHint = z.infer<typeof completionHintSchema>;

export const completionHintsSchema = z.object({
  hints: z.array(completionHintSchema),
});
export type CompletionHints = z.infer<typeof completionHintsSchema>;

export interface CompletionEvidenceFacts {
  // The latest standing reading carrying both blood pressure and pulse.
  bloodPressurePulseAt: Date | null;
  previousLabResultsAt: Date | null;
  prescriptionEvidenceAt: Date | null;
}

function shortDate(d: Date): string {
  return d.toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Stockholm",
  });
}

export function completionHintsFromFacts(
  facts: CompletionEvidenceFacts,
): CompletionHints {
  const bp = facts.bloodPressurePulseAt;
  const labs = facts.previousLabResultsAt;
  const rx = facts.prescriptionEvidenceAt;
  return {
    hints: [
      {
        item: "blood_pressure_pulse",
        present: bp !== null,
        at: bp?.toISOString() ?? null,
        // TES-1000 (Ö-244 (b)) — Emelie: the picker must show that blood
        // pressure and resting pulse
        // "saknas och att dessa normalt behövs som basunderlag".
        hint: bp
          ? `Blodtryck och vilopuls finns, mätt ${shortDate(bp)}.`
          : "Blodtryck och vilopuls saknas – behövs normalt som basunderlag.",
        preselect: false,
      },
      {
        item: "previous_lab_results",
        present: labs !== null,
        at: labs?.toISOString() ?? null,
        hint: labs
          ? `Tidigare provsvar är uppladdade (${shortDate(labs)}).`
          : "Tidigare provsvar har inte laddats upp. Bedöm om de behövs.",
        preselect: false,
      },
      {
        item: "new_blood_test",
        present: null,
        at: null,
        hint: null,
        preselect: false,
      },
      {
        item: "prescription_evidence",
        present: rx !== null,
        at: rx?.toISOString() ?? null,
        hint: rx
          ? `Dokumentation av recept/ordination är uppladdad (${shortDate(rx)}).`
          : "Dokumentation av recept/ordination har inte laddats upp.",
        preselect: false,
      },
      {
        item: "other",
        present: null,
        at: null,
        hint: "Blir en egen uppgift i patientens checklista med din rubrik och instruktion, och räknas i antalet uppgifter.",
        preselect: false,
      },
    ],
  };
}

// --- the counter -------------------------------------------------------------

// "Kompletteringar: n av m klara" (TES-941). Counted over the tasks the
// patient can act on: a `new_blood_test` locked behind an unpaid
// `lab_payment` is hidden from her and not counted, so the doctor reads the
// same number she does. TES-941:
// "Lämna blodprovet ska vara helt dolt och inte räknas i antalet synliga uppgifter."
// TES-1000 (Ö-246) — the doctor's own "Annan specificerad komplettering"
// rows count too: "ingå i 0 av X klara".
export function completionProgress(
  tasks: readonly Pick<MedicalEvidenceTask, "status" | "locked">[],
  customTasks: readonly Pick<MedicalEvidenceCustomTask, "status">[] = [],
): { done: number; total: number } {
  const visible = tasks.filter((t) => !t.locked);
  return {
    done:
      visible.filter((t) => t.status === "done").length +
      customTasks.filter((t) => t.status === "done").length,
    total: visible.length + customTasks.length,
  };
}

export function completionProgressLabel(p: {
  done: number;
  total: number;
}): string {
  return `Kompletteringar: ${p.done} av ${p.total} klara`;
}
